import { MarkedLvRow } from './lvManualMarking';
import { detectTenderNep, isTenderPositionNumber, parseTenderQuantity } from '../tenderLvImport';

/**
 * Die zweite Stufe des Markiermodus: die KI richtet die markierten Zeilen in
 * Felder aus — sie sucht die Positionen NICHT mehr.
 *
 * Das ist der eigentliche Gewinn. Beim Einlesen des ganzen PDF muss das Modell
 * zugleich finden, abgrenzen, zuordnen und formatieren; die gemeldeten Fehler
 * (verschluckte Positionen, Summenzeilen als Titel, gebrochene Reihenfolge)
 * entstehen alle beim FINDEN. Steht die Auswahl schon fest, bleibt eine eng
 * begrenzte Aufgabe, deren Ergebnis sich vollstaendig gegenprüfen laesst:
 *  - jede Antwort-Id muss eine bekannte Id sein, sonst fliegt sie raus,
 *  - fehlende Ids werden aus der lokalen Spaltenlesung aufgefuellt statt zu
 *    verschwinden,
 *  - eine lokal sicher gelesene Ordnungszahl und eine lokal gelesene Menge
 *    duerfen NICHT ueberschrieben werden. Das Modell darf aufraeumen, nicht
 *    umnummerieren und nicht umrechnen.
 *
 * Der Prompt wird hier im Client gebaut und ist damit testbar — dasselbe
 * Muster wie bei leadScoring. Der Proxy reicht ihn nur durch.
 *
 * Rein und ohne Netzwerk/DOM.
 */

export interface AlignableRow {
    id: string;
    position: string;
    bezeichnung: string;
    einheit: string;
    menge: number;
}

/** Nummeriert die markierten Zeilen durch — die Id ist der Rueckkanal. */
export const toAlignableRows = (rows: MarkedLvRow[]): AlignableRow[] =>
    rows.map((r, i) => ({
        id: String(i + 1),
        position: r.position,
        bezeichnung: r.bezeichnung,
        einheit: r.einheit,
        menge: r.menge,
    }));

export const buildLvAlignPrompt = (rows: AlignableRow[]): string => {
    const payload = JSON.stringify({
        zeilen: rows.map((r) => ({
            id: r.id,
            position: r.position,
            bezeichnung: r.bezeichnung,
            einheit: r.einheit,
            menge: r.menge,
        })),
    });

    return [
        'Du bist Kalkulator im Baugewerbe. Die folgenden Zeilen stammen aus einem Leistungsverzeichnis (LV)',
        'und wurden bereits von Hand ausgewaehlt. Deine Aufgabe ist AUSSCHLIESSLICH das Ausrichten in Felder —',
        'du suchst KEINE Positionen, du ergaenzt KEINE Zeilen und du laesst KEINE Zeile weg.',
        '',
        'Regeln:',
        '1. Gib zu JEDER Eingabe-Id GENAU EIN Objekt zurueck. Keine zusaetzlichen Ids, keine fehlenden.',
        '2. Erfinde nichts. Steht ein Wert nicht in der Zeile, bleibt das Feld leer bzw. 0.',
        '3. Aendere eine bereits gefuellte "position" (LV-/Ordnungsnummer) NICHT.',
        '4. Aendere eine bereits gefuellte "menge" (groesser 0) NICHT.',
        '5. Raeume die "bezeichnung" auf: entferne eine vorangestellte Ordnungsnummer, fuehrende Spiegelstriche',
        '   und doppelte Leerzeichen. Der Leistungstext selbst bleibt WORTGETREU erhalten — nicht kuerzen,',
        '   nicht umformulieren, nicht zusammenfassen.',
        '6. Stecken Menge und Einheit in einem Feld ("12,5 m²"), trenne sie.',
        '7. "abschnitt": der Titel/Los/Abschnitt, zu dem die Zeile gehoert, sofern er aus den Zeilen davor',
        '   erkennbar ist; sonst "".',
        '8. "assignment": ordne die Zeile EINER SiGeKo-Leistungsart zu — \'inspection\' (Baustellenbegehung/',
        '   Ueberwachung), \'sigeplan\' (SiGe-Plan erstellen/fortschreiben), \'unterlage\' (Unterlage fuer spaetere',
        '   Arbeiten/RAB 32), \'vorank\' (Vorankuendigung), \'einweisung\' (Einweisung der Beteiligten IN DEN',
        '   SiGe-Plan auf der Baustelle — NICHT die sicherheitstechnische Unterweisung nach DGUV V2, die',
        '   gehoert nicht hierher), \'asplan\' (Arbeits- und Sicherheitsplan fuer kontaminierte',
        '   Bereiche nach DGUV Regel 101-004 / TRGS 524 — ein EIGENER Plan neben dem SiGe-Plan, nur bei',
        '   Altlasten, Asbest, Schadstoffsanierung oder Bodenkontamination; im Zweifel \'sigeplan\'),',
        '   \'site_rules\' (Baustellenordnung), \'planning_general\'',
        '   (allgemeine Planungsleistung), \'additional\' (Fahrtkosten/Nebenkosten/Sonstiges), \'none\' wenn unklar.',
        '9. "nep": true bei Bedarfs-/Eventualposition, sonst false.',
        '10. Gib NIEMALS Preise aus — weder Einheits- noch Gesamtpreise.',
        '',
        'Antworte AUSSCHLIESSLICH als JSON-Objekt mit dem Feld "zeilen", einem Array. Jedes Element:',
        '{ id (string, unveraendert aus der Eingabe), position (string), bezeichnung (string), einheit (string),',
        'menge (number), abschnitt (string), assignment (string), nep (boolean) }',
        '',
        'Eingabe:',
        payload,
    ].join('\n');
};

export interface AlignedRow extends AlignableRow {
    abschnitt: string;
    assignment: string;
    nep: boolean;
}

const str = (v: unknown): string => (v === null || v === undefined ? '' : String(v)).trim();

/** Nur Buchstaben und Ziffern, kleingeschrieben — Vergleich ohne Formatierung. */
const bare = (s: string): string => s.toLowerCase().replace(/[^a-z0-9äöüß]/g, '');

/** Fuehrende Ordnungsnummer, Spiegelstriche, Aufzaehlungszeichen. */
const LEADING_NOISE = /^[\s\d.,:;)\-–—•*/]+/;

/**
 * Die aufgeraeumte Fassung der KI wird nur uebernommen, wenn sie GENAU der
 * lokale Text ist — bis auf Formatierung und bis auf einen abgeschnittenen
 * Vorspann (Ordnungsnummer, Spiegelstrich, Aufzaehlungszeichen). Alles andere
 * ist eine Umformulierung und wird verworfen.
 *
 * Regel 5 des Prompts verlangt "wortgetreu", aber eine Bitte ist keine
 * Zusicherung. Ohne diese Pruefung kaeme in Stufe 2 genau der Schaden zurueck,
 * gegen den der Markiermodus gebaut wurde — gekuerzte oder umformulierte
 * Leistungstexte —, und zwar unsichtbar, weil der Nutzer die Zeilen ja selbst
 * bestimmt hat.
 *
 * Bewusst NICHT ueber "ist enthalten in": "Begehung" steckt in
 * "Baustellenbegehung mit Protokoll", und genau diese Kuerzung soll fallen.
 */
export const acceptCleanedText = (fromAi: string, local: string): string => {
    if (!fromAi) return local;
    if (!local) return fromAi;
    const a = bare(fromAi);
    if (!a) return local;
    const full = bare(local);
    const stripped = bare(local.replace(LEADING_NOISE, ''));
    return (a === full || a === stripped) ? fromAi : local;
};

/**
 * Antwort der KI gegen die Eingabe pruefen und zusammenfuehren.
 *
 * Alles, was nicht belegt ist, faellt auf die lokale Spaltenlesung zurueck. Der
 * Aufruf kann damit nie WENIGER liefern als der reine Offline-Weg — genau das
 * macht die Stufe gefahrlos.
 */
export const mergeAlignedRows = (raw: unknown, rows: AlignableRow[]): AlignedRow[] => {
    const byId = new Map<string, Record<string, unknown>>();
    const list = (raw && typeof raw === 'object' && !Array.isArray(raw))
        ? (raw as Record<string, unknown>).zeilen
        : raw;
    if (Array.isArray(list)) {
        for (const entry of list) {
            if (!entry || typeof entry !== 'object') continue;
            const e = entry as Record<string, unknown>;
            const id = str(e.id);
            // Unbekannte Ids sind erfundene Zeilen — sie fliegen raus, statt
            // sich unbemerkt ins Angebot zu schieben.
            if (id) byId.set(id, e);
        }
    }

    return rows.map((r) => {
        const a = byId.get(r.id);
        if (!a) {
            // Auch ohne KI-Antwort die Stichwortsuche fahren: sonst zaehlt eine
            // markierte 'Bedarfsposition' voll in die Angebotssumme.
            return { ...r, abschnitt: '', assignment: 'none', nep: detectTenderNep(r.bezeichnung) };
        }

        // Eine lokal sicher gelesene Ordnungszahl gewinnt immer.
        const position = isTenderPositionNumber(r.position) ? r.position : (str(a.position) || r.position);
        // Eine lokal gelesene Menge gewinnt immer.
        const aiMenge = parseTenderQuantity(a.menge);
        const menge = r.menge > 0 ? r.menge : aiMenge;

        const bezeichnung = acceptCleanedText(str(a.bezeichnung), r.bezeichnung);
        const einheit = str(a.einheit) || r.einheit;

        return {
            id: r.id,
            position,
            bezeichnung,
            einheit,
            menge,
            abschnitt: str(a.abschnitt),
            assignment: str(a.assignment) || 'none',
            // Die KI-Antwort ODER das Stichwort im Leistungstext. Nur auf die
            // Antwort zu hoeren machte detectTenderNep im Markierpfad tot —
            // 'nep' kommt von dort immer als Boolean.
            nep: a.nep === true || detectTenderNep(bezeichnung),
        };
    });
};
